/**
 * CLOSEOUT QUEUE — the LIST page (mockups/CLOSEOUT-QUEUE-SPEC.md).
 *
 * Words-only spec (no image); FIXED items (spec §15) must match: uniform card
 * geometry + fixed slots (§1), calm-by-default / loud-on-problem signals (§2),
 * the four always-on card fields (§1), money permission (§4), My-Queue entry
 * routing (§6), bulk-send confirmation behavior (§7), fool-proof greying (§8),
 * Soft Light card treatment. The workflow stations live in ./index.tsx and are
 * untouched — this file is only the queue/list surface.
 */
import { FieldAddCounts } from "@/components/field-add/field-add-office";
import { CardHoldCallStrip, HoldChip, type HoldChipState } from "@/components/card-hold/card-hold";
import { DoneSendBack, StuckAction, TodayLine, TurnLanes, TurnLine } from "@/components/closeout/whose-turn";
import {
    HANDOFF_COLOR,
    HANDOFF_REPLY_COLOR,
    useBallSwitch,
    useWhoseTurn,
    type AskOption,
    type Ball,
    type JobNote,
    type Turn,
} from "@/lib/closeout-ball";
import { BallLine, HandoffCardTop, JobNoteStar, JobNotesDialog, NoteLine, WhyChip, handoffForMe } from "./ball";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BudgetChip } from "@/pages/customers/budget/chip";
import { workCategory } from "@/pages/customers/budget/model";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertTriangle, Loader2, Search, Star } from "lucide-react";
import { agoLabel, isStale, STALE_AFTER_DAYS, waitAgeLabel, waitDays } from "./ageModel";
import { refreshCloseOutQueue } from "./queueRefresh";
import { copyPlainTextToClipboard } from "@/fieldPanelHelpers";
import { ContactFormDialog } from "@/components/contact-form-dialog";
import { AddPoInline } from "./add-po-inline";

// ─── Types (mirror closeOutWorkspace.ts response) ───────────────────────
export type QueueGroup =
    | "review_docs"
    | "waiting_portal" // #1451: tester still has to enter the reports in the portal
    | "waiting_docs" // a report WE produce isn't on the job yet
    | "return_trip" // #1451: open return-needed flag — the field isn't done
    | "charges"
    | "payment"
    | "send"
    | "outside";

export interface QueueBlocking {
    station: 1 | 2 | 3 | 4 | 5;
    label: string;
    group: QueueGroup;
    corbin?: boolean;
}

export interface StarInfo {
    note: string | null;
    at: string;
    authorId: string | null;
    authorName: string | null;
    // Who wrote the CURRENT note text — may differ from the flagger, since any
    // office user can edit a shared star's note. This is the person to ask about
    // the comment (#319); null when there's no note.
    noteAuthorId: string | null;
    noteAuthorName: string | null;
    noteAt: string | null;
    assignedToId: string | null;
    assignedName: string | null;
}

export interface QueueJob {
    id: string;
    title: string | null;
    display_number: string | null;
    completed_at: string | null;
    // #1451: the date the queue sorts and ages by — completed_at, else the
    // first test's date, else the schedule. The server never leaves it null
    // for a real job; the client still guards so a null sorts LAST, not 1970.
    queue_sort_at: string | null;
    wo_state: "paper" | "generated" | null;
    customer_id?: string | null;
    company_name: string | null;
    site_city: string | null;
    site_name: string | null;
    service_address: string | null;
    tech_name: string | null;
    job_type_name: string | null;
    tested_devices: number;
    failed_devices: number;
    report_total: number;
    report_reviewed: number;
    // #1451: sendable documents (test reports + the generated work order)
    // still awaiting a human review. report_total/report_reviewed are the
    // reports-only pair; this is the "anything left to open?" count.
    docs_unreviewed: number;
    // #1451: devices the field marked could-not-test / not-located on this
    // job. They never get a report, so they don't hold the job on Needs
    // documents — but the card wears a chip so it stops looking like it's
    // waiting on the tech when the real work is a return trip or a note.
    untested_devices: number;
    untested: Array<{ serial: string | null; label: string | null; disposition: string; reason: string | null }>;
    // #1451: OPEN return-needed flags (newest first) — the field isn't done.
    // One or more parks the job on "Needs a return trip"; the pill tooltip
    // names each flag's device and the tech's reason.
    return_needed: number;
    return_flags: Array<{ category: string; reason: string | null; serial: string | null; at: string }>;
    // Tested devices with no invoice line. A non-blocking billing warning —
    // shows an amber "unbilled" chip on the card so the office never silently
    // forgets to bill (warn, don't block; owner decision 2026-07-21).
    unbilled_devices: number;
    /** Make It Professional #4: the newest open card decline on this job's
     *  invoices. A chip, not a star note; clears when the payment lands. */
    card_declined?: { at: string; card: string | null } | null;
    unpaid: boolean;
    star: StarInfo | null;
    blocking: QueueBlocking;
    // Number of open (unapproved) draft estimates on the job — safe for all
    // viewers (not a dollar figure). Drives whether the Estimate station shows.
    draft_estimate_count: number;
    // True when the job needs Corbin's quote/approval NOW — a draft estimate
    // awaiting him, or a failed job with no estimate yet. Puts it in his "Held
    // up on you" lane without waiting on the office's document review.
    needs_quote: boolean;
    // The online reporting portal this job's jurisdiction files through
    // (Issue #214). Drives the colour-coded pill on Needs-documents cards;
    // null when the jurisdiction isn't a portal.
    portal_name: string | null;
    // #1485: the account number for THAT portal — what the office types into
    // BSI Online / Vepo / SCTracking to find this site. Server-side this is
    // already null for a paper/offline jurisdiction (Issue #617), so
    // `portal_name && !portal_account_number` genuinely means "nobody can file
    // this yet", not "we didn't load it".
    portal_account_number: string | null;
    // #1537: a portal job still carrying a rendered paper form. Derived
    // server-side from the same condition as Closeout's portal_paper banner, so
    // the chip and the banner can never disagree, and it clears itself when the
    // job is re-rendered rather than lingering as a stored flag.
    stale_paper?: boolean;
    // #1485: who actually held the gauge — the tester on the newest test row,
    // NOT whoever the job is assigned to now (that's `tech_name`, which lies
    // after a dispatch reassignment). tester_count > 1 means the job was split
    // between techs; tester_names lists them all, newest first, for the tooltip.
    tester_name: string | null;
    tester_count: number;
    tester_names: string[];
    // Redacted to null for employee-tier viewers (§4).
    draft_estimate_total: string | null;
    open_invoice_total: string | null;
    // Total still owed incl. a not-yet-finalized draft invoice (Issue #206) —
    // the honest collectable figure when open_invoice_total is $0 but the job
    // is unpaid (a draft-only invoice).
    amount_due: string | null;
    // Who has the ball (lib/closeout-ball.ts): one holder + why, worded for
    // this viewer. Optional so older test fixtures stay valid.
    ball?: Ball | null;
    /** Whose turn: the steps this job could be handed over for (only sent
     *  to people trying it). */
    asks?: AskOption[];
    /** Whose turn: the lane, the face and the clock (only for people trying it). */
    turn?: Turn | null;
    // Job notes, shared with the office (10/16): newest first, with the
    // writer and date. Never moves the ball.
    notes?: JobNote[];
    // When the packet first reached the customer; null = not out yet.
    packet_sent_at?: string | null;
    // Not sent yet and no customer email anywhere on file (10/17): the card
    // shows "Add email" in place of "Send packet".
    no_customer_email?: boolean;
    // Not sent yet, the location needs a PO before we bill, and the job has
    // no PO number (10/20): the card shows "Add PO #" in place of "Send
    // packet", and "Send all N packets" leaves it out.
    needs_po?: boolean;
    // Any live quote on the job ("No return — replacement quoted" needs one).
    has_quote?: boolean;
    // Add from the field: tech photos/files and notes on this job (inside only).
    field_adds?: { photos: number; notes: number };
    // Card hold (#2037): Held / Won't hold / Charged / … — absent when there's nothing to say.
    card_hold?: { state: HoldChipState; amountCents: number | null } | null;
}

export interface MyQueueItem {
    kind: "estimate" | "doc_fix";
    what: string;
    blocking: string;
    blockedDays: number;
    value: number;
    deepLink: string;
}

export interface MyQueue {
    items: MyQueueItem[];
    totalValue: number;
}

export interface Staff {
    id: string;
    name: string;
    role: string;
}

export interface QueueResponse {
    jobs: QueueJob[];
    myQueue: MyQueue | null;
    canViewMoney: boolean;
    staff: Staff[];
    currentUserId: string | null;
    /** Hand to…: the approver when it is one person (optional: older servers). */
    approver?: { id: string; first_name: string } | null;
    /** Whose turn: the Today line's "done today" (only for people trying it). */
    today?: { done: number };
}

// ─── Visual language (spec "Visual language" + §2 signals) ──────────────
const COLOR = {
    frame: "#15171c",
    purple: "#6355e6",
    surface: "#f7f7f4",
    hairline: "#e6e6df",
    ink: "#20222a",
    ready: "#3fa663", // green — ready to send
    review: "#c9932e", // amber — review / office action
    waiting: "#5c6b80", // slate — waiting on documents / outside: a passive "nothing to do here" state, neutral so it never reads as the owner purple
    handoff: "#1f66b0", // blue — the "→ NAME" handed-off pill; blue is free now that "waiting" moved to slate
    owner: "#5a49d6", // purple — owner's gate
    failed: "#b93a32", // red — failed reports
    money: "#d8b24a", // gold — held-up money (FILLS only: star, confetti, progress bar)
    moneyInk: "#755c17", // the same gold as TEXT — dark enough to clear AA on the #efe4c8 chip
} as const;

// ─── Card colour = what's holding the job up RIGHT NOW ──────────────────
// One colour per card, driven by the CURRENT blocker, so the colour walks
// down the pipeline and re-colours the moment a blocker clears. The old rule
// made `failed_devices > 0` win over everything else — but a failed test is a
// permanent fact, so those cards stayed red forever no matter how far they'd
// actually moved, which is what read as "no rhyme or reason". Now RED means
// the failure still needs YOU (its quote or fix); once that's handled the card
// takes the colour of whatever is genuinely next — held-up-on-you, an office
// step, or ready — exactly like any other job. Each colour is a {spine,
// surface} pair so the 4px rail, the station circle AND the card fill all read
// as the same single colour instead of disagreeing (the old fill only ever had
// three states and often clashed with the spine).
type CardTint = { spine: string; surface: string };
const TINT: Record<"failed" | "owner" | "ready" | "review" | "waiting" | "return", CardTint> = {
    failed: { spine: COLOR.failed, surface: "#fbeeed" }, // red — the failure needs your quote/fix
    return: { spine: "#b03340", surface: "#fbeeef" }, // rose — the field isn't done: a return trip is needed (#1451)
    owner: { spine: COLOR.owner, surface: "#f1effc" }, // purple — held up on you
    ready: { spine: COLOR.ready, surface: "#eef7f0" }, // green — ready to send
    review: { spine: COLOR.review, surface: "#f8f1e2" }, // amber — office action
    waiting: { spine: COLOR.waiting, surface: "#eceef2" }, // slate — waiting on docs / outside (passive, no-action state)
};

function cardTint(j: QueueJob): CardTint {
    // #1451: a return trip is the live blocker whatever else is on the job —
    // the section it sits in and its colour agree. The FAILED pill still shows.
    if (j.blocking.group === "return_trip") return TINT.return;
    // RED only while the failure itself is the live blocker — a failed device
    // whose quote/fix still needs Corbin (an unbuilt or unapproved quote, or an
    // open report fix). Clears to the next colour the moment he's handled it.
    if (j.failed_devices > 0 && (j.needs_quote || j.blocking.corbin)) return TINT.failed;
    // Held up on you, but not a failure — a pending estimate approval or a doc fix.
    if (j.blocking.corbin || j.needs_quote) return TINT.owner;
    switch (j.blocking.group) {
        case "send":
            return TINT.ready;
        case "review_docs":
        case "charges":
        case "payment":
            return TINT.review;
        default:
            return TINT.waiting; // waiting_portal, waiting_docs, outside
    }
}

// Always-visible colour key so anyone reading the board knows what a card's
// colour means — the same {spine} colours as cardTint(), listed in the order a
// job moves (needs-you first → ready last).
const LEGEND: Array<{ color: string; label: string }> = [
    { color: TINT.failed.spine, label: "Failed — needs a quote/fix" },
    { color: TINT.return.spine, label: "Needs a return trip" },
    { color: TINT.owner.spine, label: "With owner" },
    { color: TINT.review.spine, label: "Office action" },
    { color: TINT.waiting.spine, label: "Waiting on docs / outside" },
    { color: TINT.ready.spine, label: "Ready to send" },
];

function ColorLegend() {
    return (
        <div
            className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-[11px] px-3 py-2"
            style={{ background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.08)" }}
        >
            <span className="text-[11px] font-bold uppercase tracking-[.1em] text-white/45">Color key</span>
            {LEGEND.map((l) => (
                <span
                    key={l.label}
                    className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-white/75"
                >
                    <span className="h-3 w-3 flex-none rounded-[3px]" style={{ background: l.color }} />
                    {l.label}
                </span>
            ))}
        </div>
    );
}

// The close-out pipeline, ordered closest-to-done FIRST (owner decision,
// Issue #206): Send at the top, then backwards down the line to the earliest
// stage. Every job sits in exactly one stage — its first unfinished step — so
// the queue reads as the steps of closing a job instead of one confusing pile.
// #1451 (owner decision 2026-09-10): the blanket "Needs documents" is split so
// each section NAMES the hold-up — portal reports (tester's entry), our own
// reports, and a return trip (field not done) — still closest-to-done first.
const GROUP_ORDER: QueueGroup[] = [
    "send",
    "payment",
    "charges",
    "review_docs",
    "waiting_portal",
    "waiting_docs",
    "return_trip",
    "outside",
];
// #758 (audit): the old "6…1" countdown digits were a third numbering system
// on one screen (vs the cards' circled station numbers and the workspace's
// 1–5). Sections are NAMED stages now; the station circle on each card is the
// only number, and it matches the workspace.
const GROUP_TITLES: Record<QueueGroup, { title: string; sub: string }> = {
    send: { title: "Send the packet", sub: "reviewed & paid — one click from done" },
    payment: { title: "Payment needed", sub: "collect before the packet can go" },
    charges: { title: "Estimate / invoice review", sub: "finalize the invoice, then it's ready to collect" },
    review_docs: {
        title: "Documents need review",
        sub: "open, scan, mark reviewed — then it's ready to bill",
    },
    waiting_portal: {
        title: "Waiting on portal reports",
        sub: "the tester enters them in the portal, the office pulls the PDFs — nothing to review yet",
    },
    waiting_docs: {
        title: "Waiting on our reports",
        sub: "form didn't generate or the paper report isn't uploaded yet",
    },
    return_trip: {
        title: "Needs a return trip",
        sub: "a device couldn't be tested — book the return (or dismiss the flag) and the job drops back to its normal step",
    },
    outside: { title: "Waiting on others", sub: "customer, city, or the owner — no office action yet" },
};

/** #1451: tooltip for the "↩ RETURN TRIP" pill — each open flag's device and
 *  the tech's reason, newest first. */
export function returnTitle(job: Pick<QueueJob, "return_flags" | "return_needed">): string {
    const lines = (job.return_flags ?? []).map((f) => {
        const who = f.serial ? `S/N ${f.serial}` : "job";
        const why = f.reason || f.category.replace(/_/g, " ");
        return `${who} — ${why}`;
    });
    const head = `The field isn't done: ${job.return_needed} open return-needed flag${job.return_needed === 1 ? "" : "s"}. Open the job to book the return trip, or dismiss the flag on the Grid — then this drops back to its normal step.`;
    return lines.length ? `${head}\n${lines.join("\n")}` : head;
}

/** #1451: the millisecond stamp a card sorts by — the server's queue_sort_at
 *  (completed_at → first test → schedule), falling back to completed_at for
 *  an older payload. A card with NO date sorts LAST (+Infinity), never first:
 *  `new Date(null ?? 0)` used to make it 1970 and float undated jobs to the
 *  top of every section. Exported for the queue-order test. */
export function sortStamp(job: Pick<QueueJob, "queue_sort_at" | "completed_at">): number {
    const raw = job.queue_sort_at ?? job.completed_at;
    if (!raw) return Number.POSITIVE_INFINITY;
    const t = new Date(raw).getTime();
    return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
}

/** Tooltip for the "N not tested" chip: one line per device the field
 *  couldn't test or find, with the tech's reason. */
export function untestedTitle(job: Pick<QueueJob, "untested" | "untested_devices">): string {
    const lines = (job.untested ?? []).map((u) => {
        const who = [u.label, u.serial ? `S/N ${u.serial}` : null].filter(Boolean).join(" · ") || "device";
        const why = u.disposition === "not_located" ? "can't find it" : "couldn't get to it";
        return `${who} — ${why}${u.reason ? `: ${u.reason}` : ""}`;
    });
    const head = `${job.untested_devices} device${job.untested_devices === 1 ? "" : "s"} couldn't be tested — no report is coming for ${job.untested_devices === 1 ? "it" : "them"}. Open the job to book the return trip.`;
    return lines.length ? `${head}\n${lines.join("\n")}` : head;
}

// ─── Per-portal pill colours (Issue #214) ───────────────────────────────
// Each online reporting portal gets its own readable soft-tint pill so a
// glance at the Needs-documents step tells you which portal a card waits on.
// Keyed case-insensitively on the platform name; SCT and SCTracking are the
// same portal (Swift Comply Tracking) so they share a colour. Anything not
// listed (a newly-added portal) falls back to a stable hashed colour from the
// same palette, so a pill is never blank and the same name is always the same
// colour.
type Pill = { bg: string; fg: string };
const PORTAL_PALETTE: Pill[] = [
    { bg: "#e7e3fb", fg: "#4a3cb3" }, // indigo
    { bg: "#dbe8fb", fg: "#2360ae" }, // blue
    { bg: "#d6efe8", fg: "#12735a" }, // teal
    { bg: "#e2f0d4", fg: "#4f7d17" }, // green
    { bg: "#fbe6d3", fg: "#b25b12" }, // orange
    { bg: "#f3edc7", fg: "#7d6a16" }, // amber
    { bg: "#fbdff0", fg: "#a83072" }, // pink
    { bg: "#d5f0f5", fg: "#0d7488" }, // cyan
    { bg: "#efe1f7", fg: "#8a3ca8" }, // violet
    { bg: "#efe4d5", fg: "#79572c" }, // brown
    { bg: "#fbdfe1", fg: "#b03340" }, // rose
    { bg: "#e2e6ec", fg: "#465569" }, // slate
];
const PORTAL_COLORS: Record<string, Pill> = {
    vepo: PORTAL_PALETTE[0],
    "bsi online": PORTAL_PALETTE[1],
    sctracking: PORTAL_PALETTE[2],
    sct: PORTAL_PALETTE[2], // same portal as SCTracking
    "swift comply": PORTAL_PALETTE[3],
    syncta: PORTAL_PALETTE[4],
    weirs: PORTAL_PALETTE[5],
    tokay: PORTAL_PALETTE[6],
    "aqua backflow": PORTAL_PALETTE[7],
    "vepo or sct": PORTAL_PALETTE[8],
    saws: PORTAL_PALETTE[9],
    "backflow bmp": PORTAL_PALETTE[10],
};
function portalPill(name: string): { label: string; bg: string; fg: string } {
    const key = name.trim().toLowerCase();
    let pill = PORTAL_COLORS[key];
    if (!pill) {
        // Stable hash → same unknown name always lands on the same colour.
        let h = 0;
        for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
        pill = PORTAL_PALETTE[h % PORTAL_PALETTE.length];
    }
    return { label: name.trim(), bg: pill.bg, fg: pill.fg };
}

function fmtMoney(v: string | number | null | undefined): string | null {
    if (v == null || v === "") return null;
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return null;
    return `$${Math.round(n).toLocaleString()}`;
}

function fmtDate(iso: string | null): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

// ─── Star flag: shared button + hover card ──────────────────────────────
// The hover shows exactly what the flagger wrote (the note) plus who/when and
// where it's headed — so you read the reason on hover, without clicking in.
function StarHoverContent({ star }: { star: StarInfo | null }) {
    if (!star) return <span>Flag this job / hand it off</span>;
    const who = star.authorName ?? "Someone";
    // #832 C4: the hover says the flag's AGE, not just its date — "Jul 22
    // (23d ago)" reads as stalled; "Jul 22" alone reads as handled.
    const flagAge = agoLabel(waitDays(star.at, new Date()));
    const when =
        [fmtDate(star.at), flagAge && flagAge !== "today" ? `(${flagAge})` : null].filter(Boolean).join(" ") ||
        "recently";
    // Who to ask about the comment: the note's author, which can differ from the
    // flagger once someone edits a shared star's note (#319). Only call it out
    // when it's actually a different person, so the common case stays quiet.
    const noteBy = star.note && star.noteAuthorName && star.noteAuthorId !== star.authorId ? star.noteAuthorName : null;
    return (
        <div className="space-y-1">
            <div className="flex items-center gap-1 text-[11px] font-semibold text-white/70">
                <span style={{ color: COLOR.money }}>★</span>
                <span>
                    {who} flagged · {when}
                </span>
            </div>
            {star.note ? (
                <div className="whitespace-pre-wrap text-[12.5px] leading-snug text-white">{star.note}</div>
            ) : (
                <div className="text-[12px] italic text-white/55">No note added yet</div>
            )}
            {noteBy && <div className="text-[11px] font-semibold text-white/70">✎ Note by {noteBy} — ask them</div>}
            {star.assignedName && (
                <div className="text-[11px] font-semibold text-white/70">→ Handed to {star.assignedName}</div>
            )}
        </div>
    );
}

// The star itself — icon button wrapped in a hover card. Reused on the queue
// card AND in the job-review context bar so a job can be flagged at any point
// in the review, not only from the list. Stops click propagation so it never
// triggers the card/row it sits on.
export function StarButton({
    star,
    onClick,
    className,
    iconClassName,
}: {
    star: StarInfo | null;
    onClick: () => void;
    className?: string;
    iconClassName?: string;
}) {
    const starred = !!star;
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClick();
                    }}
                    aria-label={starred ? "Edit flag or hand-off" : "Flag or hand off"}
                    className={cn("flex-none rounded p-0.5 hover:bg-black/5", className)}
                >
                    <Star
                        className={cn("h-[17px] w-[17px]", iconClassName)}
                        style={{
                            color: starred ? COLOR.money : "#b9b9ad",
                            fill: starred ? COLOR.money : "transparent",
                        }}
                    />
                </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="start" className="max-w-[280px]">
                <StarHoverContent star={star} />
            </TooltipContent>
        </Tooltip>
    );
}

function heldMoney(j: QueueJob): string | null {
    // The gold "$X" pill only shows held-up money (an unpaid balance). Fall
    // back to amount_due so a draft-only unpaid job still shows its figure.
    if (!j.unpaid) return null;
    return fmtMoney(j.open_invoice_total) ?? fmtMoney(j.amount_due);
}

function cardMoney(j: QueueJob): string | null {
    return (
        fmtMoney(j.open_invoice_total) ??
        fmtMoney(j.amount_due) ??
        (fmtMoney(j.draft_estimate_total) ? `est. ${fmtMoney(j.draft_estimate_total)}` : null)
    );
}

// Scoped CSS: page texture, progress shine, confetti — CSS/JS only, no libs.
const SCOPED_CSS = `
.co-frame{position:relative;background:${COLOR.frame};
  background-image:repeating-linear-gradient(45deg,rgba(255,255,255,.014) 0 2px,transparent 2px 9px),
    radial-gradient(1200px 420px at 88% -8%,rgba(216,178,74,.10),transparent 60%);}
.co-shine{position:absolute;inset:0;overflow:hidden;border-radius:inherit;}
.co-shine::after{content:"";position:absolute;top:0;bottom:0;width:40%;left:-45%;
  background:linear-gradient(100deg,transparent,rgba(255,255,255,.55),transparent);
  animation:co-sweep 2.6s ease-in-out infinite;}
@keyframes co-sweep{0%{left:-45%}60%,100%{left:120%}}
@keyframes co-pop{0%{transform:scale(1)}35%{transform:scale(1.16)}100%{transform:scale(1)}}
.co-pop{animation:co-pop .5s ease-out}
.co-confetti{position:fixed;inset:0;pointer-events:none;z-index:60;overflow:hidden}
.co-piece{position:absolute;top:-12px;width:9px;height:14px;border-radius:2px;animation:co-fall linear forwards}
@keyframes co-fall{0%{transform:translateY(-10px) rotate(0);opacity:1}100%{transform:translateY(104vh) rotate(560deg);opacity:.85}}
@keyframes co-banner{0%{transform:translateY(-14px);opacity:0}12%,88%{transform:translateY(0);opacity:1}100%{transform:translateY(-14px);opacity:0}}
.co-banner{animation:co-banner 3.6s ease-in-out forwards}
`;

// ═══ CONFETTI ═══════════════════════════════════════════════════════════
function Confetti({ fireKey }: { fireKey: number }) {
    const [pieces, setPieces] = useState<
        Array<{ id: number; left: number; delay: number; dur: number; color: string }>
    >([]);
    useEffect(() => {
        if (fireKey === 0) return;
        const palette = [COLOR.ready, COLOR.money, COLOR.purple, COLOR.review, COLOR.handoff];
        const next = Array.from({ length: 26 }, (_, i) => ({
            id: fireKey * 100 + i,
            left: Math.random() * 100,
            delay: Math.random() * 0.25,
            dur: 1.1 + Math.random() * 0.9,
            color: palette[i % palette.length],
        }));
        setPieces(next);
        const t = setTimeout(() => setPieces([]), 2400);
        return () => clearTimeout(t);
    }, [fireKey]);
    if (pieces.length === 0) return null;
    return (
        <div className="co-confetti" aria-hidden>
            {pieces.map((p) => (
                <span
                    key={p.id}
                    className="co-piece"
                    style={{
                        left: `${p.left}%`,
                        background: p.color,
                        animationDelay: `${p.delay}s`,
                        animationDuration: `${p.dur}s`,
                    }}
                />
            ))}
        </div>
    );
}

// ═══ GAME HEADER (§9) ═══════════════════════════════════════════════════
function GameHeader({
    canViewMoney,
    cleared,
    released,
    streak,
    goal,
    bumpKey,
    queueCount,
}: {
    canViewMoney: boolean;
    cleared: number;
    released: number;
    streak: number;
    goal: number;
    bumpKey: number;
    queueCount: number;
}) {
    // m2 (#834): before anything has been sent this sitting, "$0 · 0 streak ·
    // 0 of 12" reads as a failing grade at 9 AM. Show a neutral morning state
    // until the first send, then switch to the scoreboard.
    const morning = cleared === 0;
    const metricValue = morning ? `${queueCount}` : canViewMoney ? `$${released.toLocaleString()}` : `${cleared}`;
    const metricLabel = morning ? "to work today" : canViewMoney ? "released today" : "cleared today";
    const metricGloss = morning
        ? "Jobs in the close-out queue right now"
        : canViewMoney
          ? "Invoice dollars sent to customers since you opened this page"
          : "Jobs cleared since you opened this page";
    const title = canViewMoney ? "Closer" : "Speedster";
    const progress = Math.min(100, Math.round((cleared / Math.max(1, goal)) * 100));
    const hitGoal = cleared >= goal && goal > 0;
    const medals = [cleared >= 1 && "🥉 First clear", streak >= 3 && "🔥 On a roll", hitGoal && "🏁 Daily goal"].filter(
        Boolean,
    ) as string[];
    return (
        <div
            className="relative mb-4 overflow-hidden rounded-[13px] p-4 text-white"
            style={{
                background: "linear-gradient(120deg,#1d2130,#242a3d 60%,#2b2340)",
                border: `1px solid rgba(255,255,255,.08)`,
            }}
        >
            <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
                <div title={metricGloss} className="cursor-help">
                    <div className="text-[11px] font-bold uppercase tracking-[.12em] text-white/55">{metricLabel}</div>
                    <div key={bumpKey} className={cn("text-4xl font-black leading-none", bumpKey > 0 && "co-pop")}>
                        {metricValue}
                    </div>
                </div>
                {!morning && (
                    <div className="flex items-center gap-2">
                        <span
                            className="cursor-help rounded-full px-2.5 py-1 text-[11px] font-extrabold"
                            style={{ background: "#efe4c8", color: COLOR.moneyInk }}
                            title={
                                canViewMoney
                                    ? "Closer — you release the invoices; this header keeps score for the sitting"
                                    : "Speedster — you clear the queue; this header keeps score for the sitting"
                            }
                        >
                            ★ {title}
                        </span>
                        <span
                            className="cursor-help rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-bold"
                            title="Jobs sent in a row this sitting"
                        >
                            🔥 {streak} streak
                        </span>
                        {medals.map((m) => (
                            <span
                                key={m}
                                className="rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-white/80"
                            >
                                {m}
                            </span>
                        ))}
                    </div>
                )}
                <div className="ml-auto min-w-[220px] flex-1">
                    <div className="mb-1 flex justify-between text-[11px] font-semibold text-white/70">
                        <span>{morning ? `goal: send ${goal} today` : `${cleared} of ${goal} today`}</span>
                        <span>🏁 goal</span>
                    </div>
                    <div className="relative h-2.5 overflow-hidden rounded-full bg-white/12">
                        <div
                            className="relative h-full rounded-full transition-[width] duration-500"
                            style={{
                                width: `${progress}%`,
                                background: `linear-gradient(90deg,${COLOR.ready},${COLOR.money})`,
                            }}
                        >
                            <span className="co-shine" />
                        </div>
                    </div>
                </div>
            </div>
            {hitGoal && (
                <div
                    key={`banner-${bumpKey}`}
                    className="co-banner mt-3 rounded-lg px-3 py-1.5 text-center text-sm font-extrabold"
                    style={{ background: "rgba(63,166,99,.2)", color: "#bff0cf" }}
                >
                    🎉 Daily goal cleared — the board is burning down. Keep going!
                </div>
            )}
        </div>
    );
}

// ═══ CARD (§1 uniform geometry, §2 signals) ═════════════════════════════
function QueueCard({
    job,
    canViewMoney,
    currentUserId,
    onOpen,
    onStar,
    onSend,
    mirror,
    pinnedAbove,
    onOpenAt,
    approverId,
}: {
    /** Whose turn (slice 5): open the job at a step (a Stuck card's nudge). */
    onOpenAt?: (station: number) => void;
    approverId?: string | null;
    job: QueueJob;
    canViewMoney: boolean;
    currentUserId: string | null;
    onOpen: () => void;
    onStar: () => void;
    onSend: () => void;
    mirror?: boolean;
    /** #758: this job is ALSO showing in the pinned lane above — mark the
     *  section copy so the dual-listing reads as one job, not two. */
    pinnedAbove?: boolean;
}) {
    const ballOn = useBallSwitch().on;
    // Whose turn (slice 3): one clock (the turn line), no age chip.
    const turnOn = useWhoseTurn().on && !!job.turn;
    const qc = useQueryClient();
    const tint = cardTint(job);
    const spine = tint.spine;
    const ready = job.blocking.group === "send";
    // 10/17: nobody to send it to yet — the one button is "Add email".
    const needsEmail = ready && job.no_customer_email === true && !!job.customer_id;
    const [addEmailOpen, setAddEmailOpen] = useState(false);
    // 10/20: the location needs a PO before we bill and this job has none —
    // the one button is "Add PO #" (it comes before Add email).
    const needsPo = ready && job.needs_po === true;
    const [addPoOpen, setAddPoOpen] = useState(false);
    const failed = job.failed_devices > 0;
    // With the ball on, the gold top is "this job has a note" (shared notes).
    const starred = ballOn ? (job.notes?.length ?? 0) > 0 : !!job.star;
    // #832 M1: how long the OFFICE has had this job — the queue's real clock.
    // completed_at is when the field finished and close-out began; the card
    // used to show only the service date, so a month-old wait looked identical
    // to a fresh one. Stale (≥7d) escalates to the loud amber chip.
    // #1451: a job with no completed_at (held / cannot-complete / manual flip)
    // ages and dates from the same fallback the sort uses, so it stops showing
    // no date and no age while sitting at the top of the section.
    const cardDate = job.completed_at ?? job.queue_sort_at;
    const daysWaiting = waitDays(cardDate, new Date());
    const ageText = waitAgeLabel(daysWaiting);
    const stale = isStale(daysWaiting);
    // #832 C4: a flag that represents work shows its age too — a 23-day-old
    // star must not look like a 2-hour-old one.
    const starDays = job.star && !ballOn ? waitDays(job.star.at, new Date()) : null;
    // Stars are personal notes with the switch on: no names, no hand-offs.
    const assignedName = ballOn ? null : (job.star?.assignedName ?? null);
    const money = heldMoney(job);
    // Colour-coded portal pill on the Needs-documents step (Issue #214).
    const portal =
        (job.blocking.group === "waiting_portal" || job.blocking.group === "waiting_docs") && job.portal_name
            ? portalPill(job.portal_name)
            : null;
    // #1451: the field isn't done — an open return-needed flag. The FAILED
    // pill keeps the signal slot when both apply (the failure is the bigger
    // fact); the section + rose spine already say "return trip".
    const returnTrip = job.blocking.group === "return_trip";
    const location = job.site_name || job.company_name || job.title || "Job";
    const billing = job.company_name && job.company_name !== job.site_name ? job.company_name : null;
    const surface = tint.surface;
    // 11/4: handed to you (pink) / came back to you (green) — a thick frame
    // and a solid top bar, in place of the thin ball line.
    const forMe = ballOn ? handoffForMe(job.ball, currentUserId) : null;
    const frame = forMe === "to_me" ? HANDOFF_COLOR : forMe === "back_to_me" ? HANDOFF_REPLY_COLOR : null;
    const topWash = starred && !frame ? "linear-gradient(180deg,rgba(216,178,74,.14),transparent 42px)" : undefined;

    return (
        // The WHOLE card opens the job — clicking anywhere (incl. the empty band
        // above the pinned foot) routes to onOpen. Only the star and Send-packet
        // buttons stop propagation to keep their own action. (Issue #197: the
        // old per-slot buttons left the lower half of the card dead.)
        <div
            role="button"
            tabIndex={0}
            onClick={onOpen}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpen();
                }
            }}
            className="relative flex min-h-[172px] cursor-pointer flex-col rounded-[13px] pl-4 pr-3 py-2.5 text-left shadow-[0_1px_2px_rgba(0,0,0,.06),0_6px_16px_-8px_rgba(0,0,0,.28)] transition-shadow hover:shadow-[0_2px_4px_rgba(0,0,0,.08),0_10px_22px_-8px_rgba(0,0,0,.34)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6355e6]"
            style={{
                background: surface,
                border: frame ? `2.5px solid ${frame}` : `1px solid ${COLOR.hairline}`,
                boxShadow: frame ? `0 0 0 4px ${frame}1f, 0 8px 18px -8px ${frame}73` : undefined,
                color: COLOR.ink,
            }}
            data-handoff={forMe ?? undefined}
        >
            <span
                className="absolute inset-y-0 left-0 w-[4px] rounded-l-[13px]"
                style={{ background: frame ?? spine }}
            />
            {frame && job.ball && <HandoffCardTop ball={job.ball} me={currentUserId} />}
            {topWash && (
                <span
                    className="pointer-events-none absolute inset-x-0 top-0 h-[42px] rounded-t-[13px]"
                    style={{ background: topWash }}
                />
            )}

            {/* Slot 1 — head row: [star | id-block | signal slot] */}
            <div className="flex items-start gap-2">
                {ballOn ? (
                    <JobNoteStar notes={job.notes} onClick={onStar} className="mt-0.5" />
                ) : (
                    <StarButton star={job.star} onClick={onStar} className="mt-0.5" />
                )}
                <div className="min-w-0 flex-1 text-left">
                    <div className="truncate text-[14px] font-bold leading-tight">{location}</div>
                    <div className="truncate text-[11.5px] leading-tight" style={{ color: "#6a6c73" }}>
                        {billing ? `Billing: ${billing}` : job.job_type_name || " "}
                    </div>
                    <BudgetChip
                        customerId={job.customer_id}
                        category={workCategory(job.job_type_name ?? job.title)}
                        compact
                        className="mt-1"
                    />
                </div>
                {/* Signal slot — reserved even when empty (uniform geometry) */}
                <div className="flex min-h-[20px] min-w-[46px] flex-none justify-end">
                    {failed ? (
                        <span
                            className="whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-extrabold text-white"
                            style={{ background: COLOR.failed }}
                            title={`${job.failed_devices} ${job.failed_devices === 1 ? "device" : "devices"} failed testing on this job — each needs a repair path before the packet goes out`}
                        >
                            {job.failed_devices} FAILED {job.failed_devices === 1 ? "DEVICE" : "DEVICES"}
                        </span>
                    ) : returnTrip ? (
                        <span
                            data-testid="return-trip-pill"
                            className="whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-extrabold text-white"
                            style={{ background: TINT.return.spine }}
                            title={returnTitle(job)}
                        >
                            ↩ RETURN TRIP
                        </span>
                    ) : canViewMoney && money ? (
                        <span
                            className="rounded-full px-2 py-0.5 text-[10.5px] font-extrabold"
                            style={{ background: "#efe4c8", color: COLOR.moneyInk }}
                        >
                            {money}
                        </span>
                    ) : !canViewMoney && job.unpaid ? (
                        <span
                            className="rounded-full px-2 py-0.5 text-[10px] font-extrabold"
                            style={{ background: "#efe4c8", color: COLOR.moneyInk }}
                        >
                            UNPAID
                        </span>
                    ) : null}
                </div>
            </div>

            {/* Slot 2 — address line */}
            <div className="mt-1.5 block max-w-full truncate text-left text-[12px]" style={{ color: "#6a6c73" }}>
                📍 {job.service_address || "No service address on file"}
            </div>

            {/* Slot 3 — WO line (always shown) */}
            <div
                className="mt-1 flex flex-wrap items-center gap-1.5 text-left text-[11.5px]"
                style={{ color: "#6a6c73" }}
            >
                <span
                    className="rounded-md px-1.5 py-0.5 text-[10.5px] font-bold"
                    style={{ background: "#ecebe4", color: "#4a4c52" }}
                >
                    WO {job.display_number || "—"}
                </span>
                {/* #1485: who did the work. On EVERY card in every section — the
                    tech is a fact about the job, not about portal filing. */}
                <TechChip job={job} />
                <FieldAddCounts jobId={job.id} counts={job.field_adds} />
                {job.card_hold && (
                    <HoldChip
                        state={job.card_hold.state}
                        amountCents={job.card_hold.amountCents}
                        className="px-1.5 text-[10.5px]"
                    />
                )}
                {ageText && !turnOn && (
                    <span
                        data-testid="wait-age"
                        className="rounded-md px-1.5 py-0.5 text-[10.5px] font-bold"
                        style={
                            stale
                                ? { background: "#f8e3c9", color: "#7c4409", border: "1px solid #e0b276" }
                                : { background: "#ecebe4", color: "#4a4c52" }
                        }
                        title={
                            stale
                                ? `Waiting on the office ${daysWaiting} days — past the ${STALE_AFTER_DAYS}-day mark`
                                : "How long this job has been waiting in close-out"
                        }
                    >
                        ⏱ {ageText}
                    </span>
                )}
                <span>
                    · {job.tested_devices || 0} device{job.tested_devices === 1 ? "" : "s"}
                </span>
                {/* Non-blocking billing nudge (warn, don't block) — a tested
                    device with no invoice line. Doesn't gate the job; just keeps
                    it from being silently forgotten. */}
                {job.unbilled_devices > 0 && (
                    <span
                        className="rounded-md px-1.5 py-0.5 text-[10.5px] font-bold"
                        style={{ background: "#efe4c8", color: COLOR.moneyInk }}
                        title="A tested device isn't on the invoice yet — bill it or decline it on the Charges station."
                    >
                        ⚠ {job.unbilled_devices} unbilled
                    </span>
                )}
                {job.card_declined && (
                    <span
                        className="rounded-md bg-red-100 px-1.5 py-0.5 text-[10.5px] font-bold text-red-800 dark:bg-red-950/50 dark:text-red-200"
                        title="The card on file was declined. The customer still has the Pay button. This clears when a payment goes through."
                        data-testid="card-declined-chip"
                    >
                        ● Card declined
                        {job.card_declined.card ? ` · ${job.card_declined.card}` : ""}
                        {` · ${new Date(job.card_declined.at).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/Chicago" })}`}
                    </span>
                )}
                {/* #1451: a device the field couldn't test / couldn't find. No
                    report will ever come for it, so it doesn't hold the job on
                    Needs documents — but without this the job looked like it
                    was waiting on the tech. The tooltip names each device and
                    the tech's reason; the job view's banner books the return. */}
                {job.untested_devices > 0 && (
                    <span
                        data-testid="untested-chip"
                        className="rounded-md px-1.5 py-0.5 text-[10.5px] font-bold"
                        style={{ background: "#fbdfe1", color: "#b03340", border: "1px solid #eaa7ad" }}
                        title={untestedTitle(job)}
                    >
                        ⛔ {job.untested_devices} not tested
                    </span>
                )}
                {starDays !== null && isStale(starDays) && (
                    <span
                        data-testid="stale-flag-age"
                        className="rounded-md px-1.5 py-0.5 text-[10.5px] font-bold"
                        style={{ background: "#f8e3c9", color: "#7c4409", border: "1px solid #e0b276" }}
                        title={`Flagged ${agoLabel(starDays)} and still here — a flag is a label, not a fix`}
                    >
                        ★ flagged {agoLabel(starDays)}
                    </span>
                )}
                {fmtDate(cardDate) && <span>· {fmtDate(cardDate)}</span>}
                {canViewMoney && cardMoney(job) && !money && <span>· {cardMoney(job)}</span>}
            </div>

            {/* Slot 4 — foot pinned to bottom (aligns action lines across the row) */}
            <div className="mt-auto flex items-center gap-2 pt-2.5">
                {ready && !mirror && needsPo && addPoOpen ? (
                    <AddPoInline jobId={job.id} onClose={() => setAddPoOpen(false)} />
                ) : ready && !mirror ? (
                    <>
                        {needsPo ? (
                            <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-3 text-[12px] font-semibold"
                                data-testid="closeout-add-po"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setAddPoOpen(true);
                                }}
                            >
                                Add PO #
                            </Button>
                        ) : needsEmail ? (
                            <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-3 text-[12px] font-semibold"
                                data-testid="closeout-add-email"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setAddEmailOpen(true);
                                }}
                            >
                                Add email
                            </Button>
                        ) : (
                            <Button
                                size="sm"
                                className="h-7 px-3 text-[12px] font-semibold text-white hover:opacity-90"
                                style={{ background: COLOR.ready }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSend();
                                }}
                            >
                                Send packet
                            </Button>
                        )}
                        {assignedName && (
                            <AssignedPill name={assignedName} me={job.star?.assignedToId === currentUserId} />
                        )}
                    </>
                ) : (
                    // #1485: the foot WRAPS rather than clips. With the portal
                    // pill, the account chip and (on a mirrored card) the
                    // "pinned above" chip all in this row, a fixed single line
                    // truncated the status to "Waiting o…". The card's 172px
                    // floor means a second line usually costs nothing, and a
                    // wrapped status beats a clipped one.
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 text-left">
                        <span
                            className="flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full text-[10px] font-extrabold text-white"
                            style={{ background: spine }}
                        >
                            {mirror ? mirrorStation(job) : job.blocking.station}
                        </span>
                        {portal && (
                            <span
                                className="flex-none rounded-full px-2 py-0.5 text-[10.5px] font-extrabold"
                                style={{ background: portal.bg, color: portal.fg }}
                                title={`${portal.label} — the outside filing website this city's reports go through`}
                            >
                                Portal: {portal.label}
                            </span>
                        )}
                        {/* #1485: that portal's account number, so the outside
                            site can be searched without opening the job. */}
                        {portal && <PortalAccountChip portal={portal.label} account={job.portal_account_number} />}
                        {/* #1537: paper on a portal job — it files nowhere, and
                            the job is reachable from here instead of only being
                            found by opening it. Clears on re-render. */}
                        {job.stale_paper && (
                            <span
                                className="flex-none rounded px-1.5 py-0.5 text-[9.5px] font-extrabold uppercase"
                                style={{ background: "rgba(180,120,10,.16)", color: "#8a5a00" }}
                                title="This job carries a paper jurisdiction form, but the site files through a portal — nothing files that paper. Open the job to re-render it."
                            >
                                Paper, not filed
                            </span>
                        )}
                        <span className="truncate text-[12px] font-semibold" style={{ color: COLOR.ink }}>
                            {mirror ? mirrorLabel(job) : job.blocking.label}
                        </span>
                        {pinnedAbove && (
                            <span
                                className="flex-none rounded-full px-1.5 py-0.5 text-[9.5px] font-extrabold uppercase"
                                style={{ background: "rgba(124,111,240,.16)", color: COLOR.owner }}
                                title="This job is also pinned in the lane at the top — same job, shown twice on purpose"
                            >
                                pinned above
                            </span>
                        )}
                        {assignedName && (
                            <AssignedPill name={assignedName} me={job.star?.assignedToId === currentUserId} />
                        )}
                    </div>
                )}
            </div>
            {/* Who has the ball (10/16): the holder line on the main cards too,
                the newest shared note, and "Why isn't this out yet?". */}
            {frame ? null : turnOn && job.turn ? (
                <TurnLine turn={job.turn} />
            ) : (
                ballOn && job.ball && <BallLine ball={job.ball} />
            )}
            {ballOn && (
                <DoneSendBack
                    jobId={job.id}
                    handoff={job.ball?.handoff}
                    viewerId={currentUserId}
                    className="mt-1.5 self-start"
                />
            )}
            {turnOn && onOpenAt && (
                <StuckAction
                    jobId={job.id}
                    jobName={location}
                    turn={job.turn}
                    approverId={approverId}
                    onOpenAt={onOpenAt}
                />
            )}
            {ballOn && ((job.notes?.length ?? 0) > 0 || !job.packet_sent_at) && (
                <div className="flex items-end gap-1.5">
                    <div className="min-w-0 flex-1">
                        <NoteLine notes={job.notes} />
                    </div>
                    {!job.packet_sent_at && (
                        <span className="mt-1">
                            <WhyChip job={job} />
                        </span>
                    )}
                </div>
            )}
            {addEmailOpen && job.customer_id && (
                // The dialog portals out, but React events still bubble to the
                // card's own click (which opens the job) — stop them here.
                <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    <ContactFormDialog
                        customerId={job.customer_id}
                        title={`Add an email for ${job.company_name ?? "this customer"}`}
                        emailFirst
                        onSaved={() => refreshCloseOutQueue(qc, { now: true })}
                        onClose={() => setAddEmailOpen(false)}
                    />
                </div>
            )}
        </div>
    );
}

/** "Tristian Lopez" → "Tristian L." — the card has one line's worth of room,
 *  and a first name plus an initial is how the office says it out loud. */
export function techLabel(full: string): string {
    const parts = full.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "";
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
}

/**
 * #1485 — WHO DID THE WORK, on the card's fact row.
 *
 * Corbin's ask: "the Tech's that completed the job last, their name on the card
 * … that way i can find them easier without having to click into each one."
 * This is the tester off the newest test row, not the job's current assignee —
 * see the LATERAL in closeOutWorkspace.ts for why those differ.
 *
 * A job with no test rows yet (or only a competitor's scraped test) says so out
 * loud rather than rendering a blank slot that reads as a bug.
 */
function TechChip({ job }: { job: QueueJob }) {
    if (!job.tester_name) {
        return (
            <span
                data-testid="tech-chip"
                className="rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold"
                style={{ color: "#6a6c73", border: "1px dashed #cfcec6" }}
                title="No test has been recorded on this job yet, so we don't know who worked it. It fills in as soon as the first test lands."
            >
                👤 no tech on file
            </span>
        );
    }
    const extra = Math.max(0, job.tester_count - 1);
    const all = job.tester_names.length > 0 ? job.tester_names : [job.tester_name];
    return (
        <span
            data-testid="tech-chip"
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10.5px] font-bold"
            style={{ background: "#e3e7ef", color: "#3c4a61", border: "1px solid #cdd5e2" }}
            title={
                extra > 0
                    ? `Tested by ${all.join(", ")} — ${job.tester_name} finished it. Any of them can say where the reports stand.`
                    : `${job.tester_name} tested this job — ask them where the report stands.`
            }
        >
            👤 {techLabel(job.tester_name)}
            {extra > 0 && (
                <span
                    className="rounded-full px-1 text-[9px] font-extrabold leading-[14px] text-white"
                    style={{ background: "#3c4a61" }}
                >
                    +{extra}
                </span>
            )}
        </span>
    );
}

/**
 * #1485 — the portal's ACCOUNT NUMBER, beside the portal's name.
 *
 * The office searches BSI Online / Vepo / SCTracking by this number, so it rides
 * the portal pill and clicking it copies it straight to the clipboard — the same
 * affordance the tech-side Report Input card already gives (report-input-card.tsx).
 *
 * Only rendered where the portal pill is (Issue #617: a paper city can hold a
 * stale value in that column and must never present it as a live portal
 * account). Portal WITH no account is its own amber state — nobody can file the
 * report until the office adds it, which is worth seeing from the board.
 */
function PortalAccountChip({ portal, account }: { portal: string; account: string | null }) {
    if (!account) {
        return (
            <span
                data-testid="portal-account"
                className="flex-none rounded-md px-1.5 py-0.5 text-[10.5px] font-bold"
                style={{ background: "#f8e3c9", color: "#7c4409", border: "1px solid #e0b276" }}
                title={`This jurisdiction files through ${portal}, but the site has no portal account number on record — the report can't be filed until the office adds it (Water & Reporting on the site).`}
            >
                ⚠ ACCT missing
            </span>
        );
    }
    return (
        <button
            type="button"
            data-testid="portal-account"
            /**
             * #1490: the chip is WIDTH-CAPPED and the value truncates.
             *
             * This column is supposed to hold an account number — every real one
             * on the board is 8-16 characters — but two live sites have prose in
             * it instead ("New Location- Will need to Enter into BSI", and one
             * holding two accounts at once). Rendered verbatim those stretched
             * the chip and distorted the card's whole foot row.
             *
             * 190px clears a 16-char account with room to spare, so a normal
             * number is never cut; only the outliers are. The full value is on
             * the tooltip, and the COPY still takes `account` untouched — a chip
             * that handed someone half an account number would be worse than one
             * that looks wide.
             */
            className="inline-flex max-w-[190px] flex-none cursor-copy items-center gap-1 rounded-md px-1.5 py-0.5 text-[10.5px] font-bold tabular-nums hover:brightness-95"
            style={{ background: "#e8eaef", color: "#3c4656", border: "1px solid #d3d8e1" }}
            title={`${portal} account for this site: ${account} — click to copy`}
            onClick={async (e) => {
                // The whole card opens the job (#197); copying must not.
                e.stopPropagation();
                const ok = await copyPlainTextToClipboard(account);
                if (ok) toast.success(`Copied ${account}`);
                else toast.error("Couldn't copy that — select it by hand.");
            }}
        >
            <span className="flex-none font-extrabold opacity-60">ACCT</span>
            <span className="min-w-0 truncate">{account}</span>
            <span className="flex-none opacity-70">⧉</span>
        </button>
    );
}

function AssignedPill({ name, me }: { name: string; me: boolean }) {
    // m4 (#834): "→ YOU" was never explained — the arrow means this job's
    // close-out was handed to a specific person (assigned when starred), not
    // that they picked it up themselves.
    return (
        <span
            className="ml-auto flex-none cursor-help rounded-full px-2 py-0.5 text-[10px] font-extrabold text-white"
            style={{ background: COLOR.handoff }}
            title={
                me
                    ? "Assigned to you — this job's close-out was handed to you to finish"
                    : `Assigned to ${name} — this job's close-out is theirs to finish`
            }
        >
            → {me ? "YOU" : name.split(" ")[0]}
        </span>
    );
}

// The station Corbin's "your part" actually lives on — an estimate/quote bump
// points at station 3 even while the job's blocking station is still 1.
function mirrorStation(j: QueueJob): number {
    if (j.blocking.station === 3) return 3;
    if (j.needs_quote) return 3;
    return j.blocking.station;
}

function mirrorLabel(j: QueueJob): string {
    // #758: a pinned card is a MIRROR — the same job also sits in its normal
    // stage section below (spec decision 4b). Say so, and name the section,
    // so it never reads as two different jobs.
    const also = ` · also under “${GROUP_TITLES[j.blocking.group].title}” below`;
    if (j.blocking.station === 3) return `YOUR PART · Approve estimate →${also}`;
    if (j.blocking.station === 1 && j.blocking.corbin) return `YOUR PART · Fix the report →${also}`;
    // Bumped ahead of document review: an estimate waiting on the owner, or a
    // failed job still needing one built.
    if (j.needs_quote)
        return (j.draft_estimate_count > 0 ? "YOUR PART · Approve estimate →" : "YOUR PART · Quote this job →") + also;
    return `YOUR PART · ${j.blocking.label}${also}`;
}

// ═══ ACCOUNT STACK (§10) ════════════════════════════════════════════════
function AccountStack({
    account,
    jobs,
    canViewMoney,
    currentUserId,
    onOpen,
    onStar,
    onSend,
}: {
    account: string;
    jobs: QueueJob[];
    canViewMoney: boolean;
    currentUserId: string | null;
    onOpen: (id: string) => void;
    onStar: (j: QueueJob) => void;
    onSend: (j: QueueJob) => void;
}) {
    const [open, setOpen] = useState(false);
    const failed = jobs.reduce((s, j) => s + (j.failed_devices > 0 ? 1 : 0), 0);
    const dollars = jobs.reduce((s, j) => s + Number(j.open_invoice_total ?? 0), 0);
    return (
        <div
            className="rounded-[13px]"
            style={{ background: COLOR.surface, border: `1px solid ${COLOR.hairline}`, color: COLOR.ink }}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center gap-2 px-3.5 py-3 text-left"
            >
                <span
                    className="flex h-8 w-8 flex-none items-center justify-center rounded-lg text-[13px] font-black text-white"
                    style={{ background: COLOR.purple }}
                >
                    {jobs.length}
                </span>
                <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] font-bold">{account}</span>
                    <span className="block text-[11.5px]" style={{ color: "#6a6c73" }}>
                        {jobs.length} sites
                        {failed > 0 ? ` · ${failed} with failures` : ""}
                        {canViewMoney && dollars > 0 ? ` · $${Math.round(dollars).toLocaleString()}` : ""}
                    </span>
                </span>
                <span className="flex-none text-[12px] font-semibold" style={{ color: COLOR.purple }}>
                    {open ? "Hide" : `Open all ${jobs.length} sites →`}
                </span>
            </button>
            {open && (
                <div
                    className="grid grid-cols-1 gap-2 border-t px-2.5 py-2.5 md:grid-cols-2"
                    style={{ borderColor: COLOR.hairline }}
                >
                    {jobs.map((j) => (
                        <QueueCard
                            key={j.id}
                            job={j}
                            canViewMoney={canViewMoney}
                            currentUserId={currentUserId}
                            onOpen={() => onOpen(j.id)}
                            onStar={() => onStar(j)}
                            onSend={() => onSend(j)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

// ═══ STAR POPOVER (§3) ══════════════════════════════════════════════════
export function StarDialog({
    job,
    staff,
    currentUserId,
    onClose,
    onSaved,
}: {
    job: QueueJob;
    staff: Staff[];
    currentUserId: string | null;
    onClose: () => void;
    onSaved: () => void;
}) {
    const [note, setNote] = useState(job.star?.note ?? "");
    const [assignee, setAssignee] = useState<string | null>(job.star?.assignedToId ?? null);
    const save = useMutation({
        mutationFn: () => api.post(`/api/close-out/jobs/${job.id}/star`, { note, assignedToUserId: assignee }),
        onSuccess: () => {
            toast.success("Flagged — everyone can see it now.");
            onSaved();
            onClose();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Could not save the flag."),
    });
    const remove = useMutation({
        mutationFn: () => api.delete(`/api/close-out/jobs/${job.id}/star`),
        onSuccess: () => {
            toast.success("Flag cleared.");
            onSaved();
            onClose();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Could not clear the flag."),
    });
    // "Hand it to" chips — Me / each other office user / No one (person-picker,
    // not hardcoded to Regina/Darcy: server returns active office staff).
    const others = staff.filter((s) => s.id !== currentUserId);
    return (
        <Dialog open onClose={onClose} title="Flag & hand off" className="max-w-md">
            <div className="space-y-3">
                <div>
                    <label className="mb-1 block text-[12px] font-semibold text-foreground/70">
                        What&apos;s different / what&apos;s it holding up on?
                    </label>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="e.g. customer disputes the failed valve — needs Corbin's eyes"
                        className="h-20 w-full rounded-md border border-border bg-background p-2 text-sm"
                    />
                </div>
                <div>
                    <div className="mb-1 text-[12px] font-semibold text-foreground/70">Hand it to</div>
                    <div className="flex flex-wrap gap-1.5">
                        <Chip label="Me" on={assignee === currentUserId} onClick={() => setAssignee(currentUserId)} />
                        {others.map((s) => (
                            <Chip
                                key={s.id}
                                label={s.name.split(" ")[0]}
                                on={assignee === s.id}
                                onClick={() => setAssignee(s.id)}
                            />
                        ))}
                        <Chip label="No one" on={assignee === null} onClick={() => setAssignee(null)} />
                    </div>
                </div>
                <div className="flex items-center justify-between gap-2 pt-1">
                    {job.star ? (
                        <Button
                            variant="ghost"
                            className="text-red-600"
                            disabled={remove.isPending}
                            onClick={() => remove.mutate()}
                        >
                            {remove.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                            Remove flag
                        </Button>
                    ) : (
                        <span />
                    )}
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            className="text-white"
                            style={{ background: COLOR.purple }}
                            disabled={save.isPending}
                            onClick={() => save.mutate()}
                        >
                            {save.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                            Save
                        </Button>
                    </div>
                </div>
            </div>
        </Dialog>
    );
}

function Chip({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                "rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors",
                on ? "text-white" : "border-border bg-card text-foreground/70",
            )}
            style={on ? { background: COLOR.purple, borderColor: COLOR.purple } : undefined}
        >
            {label}
        </button>
    );
}

// ═══ BULK SEND — confirmation sheet (§7) ════════════════════════════════
interface PreviewRecipient {
    value?: string;
    contactName?: string;
}
interface PreviewDoc {
    documentType: string;
    exists: boolean;
    count: number;
}
interface SendPreview {
    recipients?: PreviewRecipient[];
    documents?: PreviewDoc[];
}

const BULK_PREVIEW_CONCURRENCY = 6;

function BulkSendSheet({
    jobs,
    canViewMoney,
    onClose,
    onSent,
}: {
    jobs: QueueJob[];
    canViewMoney: boolean;
    onClose: () => void;
    onSent: (count: number, dollars: number) => void;
}) {
    const previews = useQuery({
        queryKey: ["closeout-bulk-preview", jobs.map((j) => j.id).sort()],
        queryFn: async () => {
            // A few at a time, not all at once: "Send all" over a full lane was
            // one request per job in the same instant, which on its own could
            // eat a big slice of the per-person API allowance.
            const entries: Array<readonly [string, SendPreview | null]> = [];
            for (let i = 0; i < jobs.length; i += BULK_PREVIEW_CONCURRENCY) {
                const batch = await Promise.all(
                    jobs.slice(i, i + BULK_PREVIEW_CONCURRENCY).map(async (j) => {
                        try {
                            const p = await api.get<SendPreview>(`/api/office-review/jobs/${j.id}/send-preview`);
                            return [j.id, p] as const;
                        } catch {
                            return [j.id, null] as const;
                        }
                    }),
                );
                entries.push(...batch);
            }
            return Object.fromEntries(entries) as Record<string, SendPreview | null>;
        },
    });

    const recipientFor = (jobId: string): PreviewRecipient | null => {
        const p = previews.data?.[jobId];
        return p?.recipients && p.recipients.length > 0 ? p.recipients[0] : null;
    };
    const attachmentsFor = (jobId: string): string => {
        const docs = previews.data?.[jobId]?.documents ?? [];
        const bits = docs
            .filter((d) => d.exists)
            .map((d) => (d.count > 1 ? `${d.count} ${d.documentType}s` : d.documentType));
        return bits.length > 0 ? bits.join(" + ") : "report + invoice";
    };

    // Default: every packet with an email on file is checked.
    const [excluded, setExcluded] = useState<Set<string>>(new Set());
    const ready = previews.isSuccess;
    const held = (jobId: string) => ready && !recipientFor(jobId); // no email → held back
    const isChecked = (jobId: string) => !excluded.has(jobId) && !held(jobId);
    const toggle = (jobId: string) => {
        if (held(jobId)) return;
        setExcluded((prev) => {
            const next = new Set(prev);
            if (next.has(jobId)) next.delete(jobId);
            else next.add(jobId);
            return next;
        });
    };

    const selected = jobs.filter((j) => isChecked(j.id));
    const heldCount = jobs.filter((j) => held(j.id)).length;
    const recipientCount = new Set(selected.map((j) => recipientFor(j.id)?.value).filter(Boolean)).size;

    const send = useMutation({
        mutationFn: async () => {
            let ok = 0;
            let dollars = 0;
            const failedJobs: Array<{ name: string; why: string | null }> = [];
            // No bulk endpoint exists — fan out to the sanctioned per-job send.
            for (const j of selected) {
                try {
                    await api.post(`/api/office-review/jobs/${j.id}/approve-and-send`, {});
                    ok += 1;
                    dollars += Number(j.open_invoice_total ?? 0);
                } catch (e: any) {
                    // #1250 — a bare catch turned every refusal into "needs a
                    // look", so an office user had no idea a permit was the
                    // blocker on 12 of 40 jobs. Keep the server's reason.
                    const why =
                        e?.body?.reason === "permit_not_answered"
                            ? "needs a permit answer"
                            : (e?.body?.message ?? null);
                    failedJobs.push({ name: j.site_name || j.company_name || j.id, why });
                }
            }
            return { ok, dollars, failedJobs };
        },
        onSuccess: ({ ok, dollars, failedJobs }) => {
            if (ok > 0) toast.success(`${ok} packet${ok === 1 ? "" : "s"} sent.`);
            if (failedJobs.length > 0) {
                const permitBlocked = failedJobs.filter((f) => f.why === "needs a permit answer");
                if (permitBlocked.length === failedJobs.length) {
                    toast.error(
                        `${failedJobs.length} job${failedJobs.length === 1 ? "" : "s"} still need${failedJobs.length === 1 ? "s" : ""} a permit answer: ${permitBlocked
                            .slice(0, 3)
                            .map((f) => f.name)
                            .join(", ")}. Open the job and answer it on the Permit card.`,
                    );
                } else {
                    toast.error(
                        `${failedJobs.length} couldn't send: ${failedJobs
                            .slice(0, 3)
                            .map((f) => (f.why ? `${f.name} (${f.why})` : f.name))
                            .join(", ")}`,
                    );
                }
            }
            onSent(ok, canViewMoney ? dollars : 0);
            onClose();
        },
        onError: () => toast.error("Send failed."),
    });

    return (
        <Dialog
            open
            onClose={onClose}
            title={`Send ${jobs.length} packet${jobs.length === 1 ? "" : "s"} — confirm each one`}
            className="max-w-2xl"
        >
            <div className="space-y-3">
                <p className="text-[12.5px] text-foreground/70">
                    Nothing sends until you press the button below. Uncheck any packet to hold it. Packets with no email
                    on file are held automatically.
                </p>
                {previews.isLoading ? (
                    <div className="flex items-center gap-2 p-6 text-sm text-foreground/70">
                        <Loader2 className="h-4 w-4 animate-spin" /> Resolving recipients…
                    </div>
                ) : (
                    <div className="max-h-[46vh] space-y-2 overflow-y-auto pr-1">
                        {jobs.map((j) => {
                            const rec = recipientFor(j.id);
                            const isHeld = held(j.id);
                            return (
                                <div
                                    key={j.id}
                                    className={cn(
                                        "flex items-start gap-2.5 rounded-lg border p-2.5 text-[12.5px]",
                                        isHeld ? "border-amber-300 bg-amber-50 dark:bg-amber-900/20" : "border-border",
                                    )}
                                >
                                    <input
                                        type="checkbox"
                                        className="mt-0.5 h-4 w-4"
                                        checked={isChecked(j.id)}
                                        disabled={isHeld}
                                        onChange={() => toggle(j.id)}
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="font-bold">{j.site_name || j.company_name || "Job"}</span>
                                            <span className="rounded bg-muted px-1.5 text-[10.5px] font-bold text-foreground/60">
                                                WO {j.display_number || "—"}
                                            </span>
                                        </div>
                                        {isHeld ? (
                                            <div className="mt-0.5 flex items-center gap-1 font-semibold text-amber-700 dark:text-amber-400">
                                                <AlertTriangle className="h-3 w-3" /> No email — held back. Add one to
                                                send, or it prints for mail instead.
                                            </div>
                                        ) : (
                                            <>
                                                <div className="mt-0.5 truncate text-foreground/70">
                                                    To: {rec?.contactName ? `${rec.contactName} ` : ""}
                                                    &lt;{rec?.value}&gt;
                                                </div>
                                                <div className="truncate text-foreground/55">
                                                    📎 {attachmentsFor(j.id)}
                                                    {canViewMoney && fmtMoney(j.open_invoice_total)
                                                        ? ` · ${fmtMoney(j.open_invoice_total)}`
                                                        : j.unpaid
                                                          ? " · unpaid"
                                                          : " · paid"}
                                                </div>
                                                <div className="truncate text-muted-foreground">
                                                    Subject: “{j.site_name || j.company_name} — your service documents”
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
                    <span className="text-[12px] font-semibold text-foreground/70">
                        {selected.length} of {jobs.length} selected · goes to {recipientCount} recipient
                        {recipientCount === 1 ? "" : "s"}
                        {heldCount > 0 ? ` · ${heldCount} held` : ""}
                    </span>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            className="text-white"
                            style={{ background: COLOR.purple }}
                            disabled={!ready || selected.length === 0 || send.isPending}
                            title={selected.length === 0 ? "Nothing selected to send" : undefined}
                            onClick={() => send.mutate()}
                        >
                            {send.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                            Send {selected.length} packet{selected.length === 1 ? "" : "s"}
                        </Button>
                    </div>
                </div>
            </div>
        </Dialog>
    );
}

// ═══ TOP LANE (§5) ══════════════════════════════════════════════════════
function LaneHeading({ title, sub, tone }: { title: string; sub: string; tone: string }) {
    return (
        <div className="mb-2 flex items-baseline gap-2">
            <span className="text-[12px] font-extrabold uppercase tracking-[.08em]" style={{ color: tone }}>
                {title}
            </span>
            <span className="text-[11.5px] text-white/55">— {sub}</span>
        </div>
    );
}

// ═══ MAIN LIST ══════════════════════════════════════════════════════════
export function CloseOutList({
    data,
    isLoading,
    onOpen,
}: {
    data: QueueResponse | undefined;
    isLoading: boolean;
    onOpen: (id: string, station?: number) => void;
}) {
    const { user } = useAuth();
    const qc = useQueryClient();
    const navigate = useNavigate();
    const isOwner = (user?.role ?? "").toLowerCase() === "admin";
    const canViewMoney = data?.canViewMoney ?? false;
    const currentUserId = data?.currentUserId ?? user?.id ?? null;
    const staff = data?.staff ?? [];
    const jobs = useMemo(() => data?.jobs ?? [], [data]);

    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("all");
    const [sort, setSort] = useState<"urgent" | "oldest" | "money">("oldest");
    const [byAccount, setByAccount] = useState(false);
    const [starTarget, setStarTarget] = useState<QueueJob | null>(null);
    const ballOn = useBallSwitch().on;
    // Whose turn (slice 3): the list splits by whose turn it is.
    const turnOn = useWhoseTurn().on && jobs.some((j) => !!j.turn);
    const [bulkJobs, setBulkJobs] = useState<QueueJob[] | null>(null);

    // Session game state (§9) — CSS/JS only, no localStorage.
    const [cleared, setCleared] = useState(0);
    const [released, setReleased] = useState(0);
    const [streak, setStreak] = useState(0);
    const [bumpKey, setBumpKey] = useState(0);
    const [confettiKey, setConfettiKey] = useState(0);
    const goal = 12;

    const onSent = (count: number, dollars: number) => {
        if (count <= 0) return;
        setCleared((c) => c + count);
        setReleased((d) => d + Math.round(dollars));
        setStreak((s) => s + count);
        setBumpKey((k) => k + 1);
        setConfettiKey((k) => k + 1);
        refreshCloseOutQueue(qc, { now: true });
    };

    // Tabs mirror the pipeline sections 1:1 (Issue #206), in the same
    // closest-to-done order as GROUP_ORDER, framed by "All movable" and the two
    // cross-cutting views (Failed / Starred) that span every stage.
    const FILTERS = useMemo(
        () => [
            { key: "all", label: "All movable", match: () => true, always: true },
            ...GROUP_ORDER.map((g) => ({
                key: g,
                label: GROUP_TITLES[g].title,
                match: (j: QueueJob) => j.blocking.group === g,
                always: false,
            })),
            { key: "failed", label: "⚠ Failed reports", match: (j: QueueJob) => j.failed_devices > 0, always: true },
            ballOn
                ? {
                      key: "starred",
                      label: "★ Has a note",
                      match: (j: QueueJob) => (j.notes?.length ?? 0) > 0,
                      always: true,
                  }
                : { key: "starred", label: "★ Starred", match: (j: QueueJob) => !!j.star, always: true },
        ],
        [ballOn],
    );
    const activeFilter = FILTERS.find((f) => f.key === filter) ?? FILTERS[0];

    const term = search.trim().toLowerCase();
    let visible = jobs.filter(activeFilter.match);
    if (term) {
        visible = visible.filter((j) =>
            [j.site_name, j.company_name, j.service_address, j.display_number]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(term)),
        );
    }
    visible = [...visible].sort((a, b) => {
        if (sort === "money" && canViewMoney) {
            const av = Number(a.amount_due ?? a.open_invoice_total ?? 0) + Number(a.draft_estimate_total ?? 0);
            const bv = Number(b.amount_due ?? b.open_invoice_total ?? 0) + Number(b.draft_estimate_total ?? 0);
            if (bv !== av) return bv - av;
        }
        if (sort === "urgent") {
            const af = a.failed_devices > 0 ? 1 : 0;
            const bf = b.failed_devices > 0 ? 1 : 0;
            if (bf !== af) return bf - af;
        }
        // Default within-group: oldest work first. #1451: sort by the server's
        // queue_sort_at (completed_at → first test → schedule) and put a
        // dateless card LAST — `new Date(null ?? 0)` made it 1970 and floated
        // undated jobs to the top of every section, which read as "not sorted".
        return sortStamp(a) - sortStamp(b);
    });

    // Top lane sources. Corbin's lane also pulls in jobs that need a quote even
    // when the sequential blocking logic still parks them on document review —
    // he quotes off the field report and shouldn't wait on the office.
    // Whose turn (slice 3) has its own lanes: no pinned lane, no card twice.
    const ownerHeldUp = turnOn ? [] : jobs.filter((j) => j.blocking.corbin || j.needs_quote);
    const readyToClear = turnOn ? [] : visible.filter((j) => j.blocking.group === "send");
    // 10/20: a job still waiting on its PO number stays in the lane (with its
    // Add PO # button) but is never part of "Send all".
    const sendAll = readyToClear.filter((j) => j.needs_po !== true);
    // #758: jobs shown in the pinned lane above. Section copies get a "pinned
    // above" marker and the stage tabs stop counting them twice.
    const pinnedIds = new Set((isOwner ? ownerHeldUp : readyToClear).map((j) => j.id));

    const openStar = (j: QueueJob) => setStarTarget(j);
    const sendOne = (j: QueueJob) => setBulkJobs([j]);
    const refetch = () => refreshCloseOutQueue(qc, { now: true });

    return (
        <div className="co-frame overflow-hidden rounded-[16px] p-3 sm:p-4">
            <style dangerouslySetInnerHTML={{ __html: SCOPED_CSS }} />
            {!turnOn && <Confetti fireKey={confettiKey} />}
            {/* Card hold (#2037): "Card won't hold — call the customer", same rows as the booking chip. */}
            <div className="mb-3 empty:hidden">
                <CardHoldCallStrip onOpenJob={(id) => navigate(`/jobs/${id}`)} />
            </div>

            {isLoading ? (
                <div
                    className="mb-4 h-[118px] animate-pulse rounded-[13px]"
                    style={{ background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.07)" }}
                    aria-hidden
                />
            ) : turnOn ? (
                // Whose turn (slice 4): one calm Today line, no scoreboard.
                <TodayLine jobs={jobs} done={data?.today?.done ?? 0} />
            ) : (
                <GameHeader
                    canViewMoney={canViewMoney}
                    cleared={cleared}
                    released={released}
                    streak={streak}
                    goal={goal}
                    bumpKey={bumpKey}
                    queueCount={jobs.length}
                />
            )}

            {/* Search (§11) */}
            <div
                className="mb-3 flex items-center gap-2 rounded-[11px] px-3 py-2"
                style={{ background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.08)" }}
            >
                <Search className="h-4 w-4 text-white/50" />
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search location, address, or WO#  —  “Cintas Plano”, “76102”, “072654691”"
                    className="w-full bg-transparent text-[13px] text-white placeholder:text-white/40 focus:outline-none"
                />
                {search && (
                    <button
                        onClick={() => setSearch("")}
                        className="text-[11px] font-semibold text-white/50 hover:text-white/80"
                    >
                        clear
                    </button>
                )}
            </div>

            {/* Filters + sort + toggles (§12, §13) */}
            <div className="mb-3 flex flex-wrap items-center gap-1.5">
                {FILTERS.map((f) => {
                    const n = jobs.filter((j) => f.match(j) && !pinnedIds.has(j.id)).length;
                    // Hide an empty stage tab (keep All / Failed / Starred and
                    // the currently-selected tab always visible).
                    if (!f.always && n === 0 && filter !== f.key) return null;
                    const on = filter === f.key;
                    return (
                        <button
                            key={f.key}
                            onClick={() => setFilter(f.key)}
                            className={cn(
                                "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold transition-colors",
                                on ? "text-white" : "text-white/70",
                            )}
                            style={
                                on
                                    ? { background: COLOR.purple }
                                    : { background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.08)" }
                            }
                        >
                            {f.label}
                            <span
                                className="rounded-full px-1.5 text-[10.5px] font-extrabold"
                                style={{ background: on ? "rgba(255,255,255,.25)" : "rgba(255,255,255,.12)" }}
                            >
                                {n}
                            </span>
                        </button>
                    );
                })}
                <div className="ml-auto flex items-center gap-1.5">
                    <button
                        onClick={() => setByAccount((v) => !v)}
                        className={cn(
                            "rounded-full px-3 py-1.5 text-[12px] font-semibold",
                            byAccount ? "text-white" : "text-white/70",
                        )}
                        style={
                            byAccount
                                ? { background: COLOR.purple }
                                : { background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.08)" }
                        }
                    >
                        Group by account
                    </button>
                    <button
                        onClick={() => toast("Review mode — go one-by-one is coming soon.")}
                        className="rounded-full px-3 py-1.5 text-[12px] font-semibold text-white/70"
                        style={{ background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.08)" }}
                    >
                        ▶ Review mode
                    </button>
                    <select
                        value={sort}
                        onChange={(e) => setSort(e.target.value as typeof sort)}
                        className="rounded-full px-3 py-1.5 text-[12px] font-semibold text-white/80"
                        style={{ background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.08)" }}
                    >
                        {isLoading ? null : canViewMoney ? (
                            <option value="money">Most money first</option>
                        ) : (
                            <option value="urgent">Most urgent</option>
                        )}
                        <option value="oldest">Oldest first</option>
                    </select>
                </div>
            </div>

            {/* Colour key (owner request) — what each card colour means */}
            <ColorLegend />

            {/* Top lane (§5) */}
            {isOwner
                ? ownerHeldUp.length > 0 && (
                      <div
                          className="mb-4 rounded-[13px] p-3"
                          style={{ background: "rgba(124,111,240,.1)", border: `1px solid ${COLOR.owner}` }}
                      >
                          <LaneHeading
                              title="Held up on you"
                              sub="jobs that need your hand — approvals, fixes, and quotes off the report — the rest keeps moving"
                              tone={COLOR.owner}
                          />
                          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 2xl:grid-cols-3">
                              {ownerHeldUp.map((j) => (
                                  <QueueCard
                                      key={j.id}
                                      job={j}
                                      mirror
                                      canViewMoney={canViewMoney}
                                      currentUserId={currentUserId}
                                      // A quote-needing job opens straight on the
                                      // Estimate station so he can price it off the
                                      // report — no detour through document review.
                                      onOpen={() =>
                                          onOpen(j.id, j.needs_quote && j.blocking.station !== 3 ? 3 : undefined)
                                      }
                                      onStar={() => openStar(j)}
                                      onSend={() => sendOne(j)}
                                  />
                              ))}
                          </div>
                      </div>
                  )
                : readyToClear.length > 0 && (
                      <div
                          className="mb-4 rounded-[13px] p-3"
                          style={{ background: "rgba(63,166,99,.1)", border: `1px solid ${COLOR.ready}` }}
                      >
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                              <LaneHeading
                                  title="Ready to clear — low-hanging fruit"
                                  sub="reviewed & paid, one doc away"
                                  tone={COLOR.ready}
                              />
                              {sendAll.length > 0 && (
                                  <Button
                                      size="sm"
                                      className="ml-auto text-white"
                                      style={{ background: COLOR.ready }}
                                      data-testid="closeout-send-all"
                                      onClick={() => setBulkJobs(sendAll)}
                                  >
                                      Send all {sendAll.length} packets
                                  </Button>
                              )}
                          </div>
                          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 2xl:grid-cols-3">
                              {readyToClear.map((j) => (
                                  <QueueCard
                                      key={j.id}
                                      job={j}
                                      canViewMoney={canViewMoney}
                                      currentUserId={currentUserId}
                                      onOpen={() => onOpen(j.id)}
                                      onStar={() => openStar(j)}
                                      onSend={() => sendOne(j)}
                                  />
                              ))}
                          </div>
                      </div>
                  )}

            {/* Full queue */}
            {isLoading ? (
                <div className="flex items-center gap-2 p-6 text-sm text-white/70">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading queue…
                </div>
            ) : visible.length === 0 ? (
                <p className="p-6 text-sm text-white/60">Nothing matches — the board is clear here. 🎉</p>
            ) : turnOn && !byAccount ? (
                <TurnLanes
                    jobs={visible}
                    renderCard={(j) => (
                        <QueueCard
                            job={j}
                            canViewMoney={canViewMoney}
                            currentUserId={currentUserId}
                            onOpen={() => onOpen(j.id)}
                            onStar={() => openStar(j)}
                            onSend={() => sendOne(j)}
                            onOpenAt={(station) => onOpen(j.id, station)}
                            approverId={data?.approver?.id ?? null}
                        />
                    )}
                />
            ) : byAccount ? (
                <AccountGrouped
                    jobs={visible}
                    canViewMoney={canViewMoney}
                    currentUserId={currentUserId}
                    onOpen={onOpen}
                    onStar={openStar}
                    onSend={sendOne}
                />
            ) : (
                <StationGrouped
                    jobs={visible}
                    pinnedIds={pinnedIds}
                    canViewMoney={canViewMoney}
                    currentUserId={currentUserId}
                    onOpen={onOpen}
                    onStar={openStar}
                    onSend={sendOne}
                />
            )}

            {starTarget && ballOn && (
                <JobNotesDialog
                    jobId={starTarget.id}
                    jobName={starTarget.site_name || starTarget.company_name || starTarget.title || "this job"}
                    onClose={() => setStarTarget(null)}
                />
            )}
            {starTarget && !ballOn && (
                <StarDialog
                    job={starTarget}
                    staff={staff}
                    currentUserId={currentUserId}
                    onClose={() => setStarTarget(null)}
                    onSaved={refetch}
                />
            )}
            {bulkJobs && (
                <BulkSendSheet
                    jobs={bulkJobs}
                    canViewMoney={canViewMoney}
                    onClose={() => setBulkJobs(null)}
                    onSent={onSent}
                />
            )}
        </div>
    );
}

function StationGrouped({
    jobs,
    canViewMoney,
    currentUserId,
    onOpen,
    onStar,
    onSend,
    pinnedIds,
}: {
    jobs: QueueJob[];
    canViewMoney: boolean;
    currentUserId: string | null;
    onOpen: (id: string) => void;
    onStar: (j: QueueJob) => void;
    onSend: (j: QueueJob) => void;
    pinnedIds?: Set<string>;
}) {
    const grouped = GROUP_ORDER.map((g) => ({ group: g, rows: jobs.filter((j) => j.blocking.group === g) })).filter(
        (g) => g.rows.length > 0,
    );
    return (
        <div className="space-y-4">
            {grouped.map(({ group, rows }) => (
                <div key={group}>
                    <LaneHeading
                        title={GROUP_TITLES[group].title}
                        sub={`${rows.length} · ${GROUP_TITLES[group].sub}`}
                        tone="rgba(255,255,255,.85)"
                    />
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2 2xl:grid-cols-3">
                        {rows.map((j) => (
                            <QueueCard
                                key={j.id}
                                job={j}
                                pinnedAbove={pinnedIds?.has(j.id)}
                                canViewMoney={canViewMoney}
                                currentUserId={currentUserId}
                                onOpen={() => onOpen(j.id)}
                                onStar={() => onStar(j)}
                                onSend={() => onSend(j)}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function AccountGrouped({
    jobs,
    canViewMoney,
    currentUserId,
    onOpen,
    onStar,
    onSend,
}: {
    jobs: QueueJob[];
    canViewMoney: boolean;
    currentUserId: string | null;
    onOpen: (id: string) => void;
    onStar: (j: QueueJob) => void;
    onSend: (j: QueueJob) => void;
}) {
    const byAcct = new Map<string, QueueJob[]>();
    for (const j of jobs) {
        const key = j.company_name || j.site_name || "Other";
        const arr = byAcct.get(key) ?? [];
        arr.push(j);
        byAcct.set(key, arr);
    }
    const entries = [...byAcct.entries()].sort((a, b) => b[1].length - a[1].length);
    return (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {entries.map(([account, rows]) =>
                rows.length > 1 ? (
                    <AccountStack
                        key={account}
                        account={account}
                        jobs={rows}
                        canViewMoney={canViewMoney}
                        currentUserId={currentUserId}
                        onOpen={onOpen}
                        onStar={onStar}
                        onSend={onSend}
                    />
                ) : (
                    <QueueCard
                        key={account}
                        job={rows[0]}
                        canViewMoney={canViewMoney}
                        currentUserId={currentUserId}
                        onOpen={() => onOpen(rows[0].id)}
                        onStar={() => onStar(rows[0])}
                        onSend={() => onSend(rows[0])}
                    />
                ),
            )}
        </div>
    );
}
